import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  enqueueReminders,
  listConsentPreferences,
  setConsentPreference,
} from './reminders-api';
import type { ConsentPreference, ReminderCandidate, ReminderChannel } from './types';

export const reminderKeys = {
  all: ['reminders'] as const,
  consent: (customerId: string) => [...reminderKeys.all, 'consent', customerId] as const,
};

export function useEnqueueReminders() {
  return useMutation<ReminderCandidate[], Error, string>({
    mutationFn: enqueueReminders,
  });
}

export function useConsentPreferences(customerId: string | undefined) {
  return useQuery({
    queryKey: reminderKeys.consent(customerId ?? '__none__'),
    queryFn: () => {
      if (!customerId) return Promise.resolve([]);
      return listConsentPreferences(customerId);
    },
    enabled: Boolean(customerId),
  });
}

export function useSetConsentPreference(customerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ConsentPreference,
    Error,
    { channel: ReminderChannel; optIn: boolean }
  >({
    mutationFn: ({ channel, optIn }) => {
      if (!customerId) throw new Error('customerId is required');
      return setConsentPreference(customerId, channel, optIn);
    },
    onSuccess: () => {
      if (customerId) {
        qc.invalidateQueries({ queryKey: reminderKeys.consent(customerId) });
      }
    },
  });
}
